import React, { useEffect, useState } from "react";
import { XIcon, InboxIcon } from "lucide-react";
import createDOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ResizableHandle, ResizablePanel } from "@/components/ui/resizable";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import EmptyState from "@/components/ui/empty-state";
import AmyBadge from "@/components/ui/amy-badge";
import { CreatorInboxSkeleton } from "@/components/dashboard_campaign/inbox/CreatorInboxSkeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { cleanHtml, formatPlainText } from "@/utils/emailFormatters";
import { useShareStore, ShareConversationMessage } from "@/stores/shareStore";
import { Frame, FramePanel } from "@/components/ui/frame";

function ShareMessage({ message }: { message: ShareConversationMessage }) {
  const DOMPurify = React.useMemo(() => createDOMPurify(window), []);
  const isCreator = message.side === "creator";
  const creatorName = message.sender_name || "Creator";
  // Both real email threads (Gmail/Outlook) and generated demo threads arrive as
  // HTML, so everything renders through the same sanitized-HTML pipeline. This
  // keeps demos visually identical to real threads and respects <br>/<p> spacing
  // (Markdown collapsed single newlines, mangling signatures like "Best,\nAmy").

  return (
    <div className="flex flex-col overflow-hidden p-5 pb-10">
      <div className="flex flex-row items-center w-full gap-5">
        {isCreator ? (
          <>
            <Avatar className="size-10">
              <AvatarFallback className="bg-primary/15 text-primary border border-primary/60">
                <span>{creatorName.charAt(0).toUpperCase()}</span>
              </AvatarFallback>
              {message.avatar && <AvatarImage src={message.avatar} />}
            </Avatar>
            <p className="font-medium">{creatorName}</p>
          </>
        ) : (
          <AmyBadge className="p-0.5 pr-2.5" imgClassName="size-8" />
        )}
        {message.sent_at && (
          <span className="text-xs text-foreground/70 text-nowrap ml-auto">
            <span>Sent</span> <span>{message.sent_at}</span>
          </span>
        )}
      </div>
      <div
        translate="yes"
        className="notranslate-exempt text-sm ml-15 mt-2 overflow-auto w-auto min-w-sm border-none shadow-none text-foreground/90 format-prose max-w-xl [&_a]:text-primary [&_a]:underline [&_a]:hover:decoration-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_img]:max-w-full [&_img]:h-auto [&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(cleanHtml(formatPlainText(message.content))),
        }}
      />
    </div>
  );
}

function ShareConversation({
  token,
  campaignCreatorId,
  onClose,
}: {
  token: string;
  campaignCreatorId: string;
  onClose: () => void;
}) {
  const { fetchSharedThread } = useShareStore();
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState<string | null>(null);
  const [messages, setMessages] = useState<ShareConversationMessage[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await fetchSharedThread(token, campaignCreatorId);
      if (!cancelled) {
        setSubject(res.subject);
        setMessages(res.messages);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, campaignCreatorId, fetchSharedThread]);

  return (
    // Same shell as the table: gray frame, ghost toolbar, white panel.
    <Frame className="h-full">
      <div className="flex items-center gap-1 p-1">
        <Button
          onClick={onClose}
          variant="ghost"
          size="icon-sm"
         
          title="Close conversation"
        >
          <XIcon className="size-4" />
        </Button>
      </div>
      <FramePanel className="flex-1 min-h-0 overflow-hidden p-0">
        {loading ? (
          <ScrollArea className="w-full h-full bg-background">
            <CreatorInboxSkeleton />
          </ScrollArea>
        ) : !messages.length ? (
          <div className="flex items-center justify-center h-full">
            <EmptyState icon={<InboxIcon />} text="No conversation yet" />
          </div>
        ) : (
          <ScrollArea className="flex flex-col gap-5 w-full h-full bg-background z-10">
            <div className="py-2 px-3 text-sm font-medium text-foreground/90 flex flex-col border-b border-dashed">
              <span className="text-xs text-muted-foreground font-normal">Subject: </span>
              {subject || "Conversation"}
            </div>
            <div className="divide-y">
              {messages.map((message, index) => (
                <ShareMessage key={index} message={message} />
              ))}
            </div>
          </ScrollArea>
        )}
      </FramePanel>
    </Frame>
  );
}

/** Read-only conversation side panel for a public share link. */
export default function ShareCreatorRightPanel({
  selectedCreatorId,
  onClose,
  token,
}: {
  selectedCreatorId: string | undefined;
  onClose: () => void;
  token: string;
}) {
  const isMobile = useIsMobile();

  const panelContent = selectedCreatorId && (
    <ShareConversation
      token={token}
      campaignCreatorId={selectedCreatorId}
      onClose={onClose}
    />
  );

  if (isMobile) {
    return (
      <Drawer open={!!selectedCreatorId} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent className="h-[95vh] pb-6">
          {panelContent}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <>
      <ResizableHandle
        withHandle
        className={`bg-transparent hover:bg-primary mx-0.5 w-0.75 rounded-pill ${
          selectedCreatorId ? "" : "hidden"
        }`}
      />
      <ResizablePanel
        defaultSize={45}
        minSize={30}
        className={selectedCreatorId ? "" : "hidden"}
      >
        {panelContent}
      </ResizablePanel>
    </>
  );
}
